/** Small helpers around the /config chain and asset lists. */
import type { AssetInfo, ChainInfo, KeyFamily, NetworkMode } from "@custos/api-client";

export const FAMILY_KEY: Record<ChainInfo["family"], KeyFamily> = {
  evm: "EVM",
  tron: "TRON",
  bitcoin: "BITCOIN",
  solana: "SOLANA",
};

export const assetKey = (chain: string, symbol: string) => `${chain}:${symbol}`;

/** "Ethereum · Sepolia" — shown next to every address and amount so the network is never ambiguous. */
export function networkLabel(chain: ChainInfo | undefined, fallback = ""): string {
  if (!chain) return fallback;
  return chain.displayName === chain.network ? chain.displayName : `${chain.displayName} · ${chain.network}`;
}

export function findChain(chains: ChainInfo[], key: string): ChainInfo | undefined {
  return chains.find((c) => c.key === key);
}

export function findAsset(assets: AssetInfo[], chain: string, symbol: string): AssetInfo | undefined {
  return assets.find((a) => a.chain === chain && a.symbol === symbol);
}

const BASE58 = "[1-9A-HJ-NP-Za-km-z]";

/**
 * Quick format check so the user can't paste an address of the wrong kind (network-mismatch
 * protection, spec §2). The API does the full check (checksums, Bitcoin network) again.
 */
export function looksLikeAddress(family: ChainInfo["family"], address: string, mode: NetworkMode): boolean {
  const a = address.trim();
  switch (family) {
    case "evm":
      return /^0x[0-9a-fA-F]{40}$/.test(a);
    case "tron":
      return new RegExp(`^T${BASE58}{33}$`).test(a);
    case "bitcoin":
      return mode === "testnet"
        ? /^(tb1[02-9ac-hj-np-z]{8,87}|[mn2]\w{25,34})$/i.test(a)
        : /^(bc1[02-9ac-hj-np-z]{8,87}|[13]\w{25,34})$/i.test(a);
    case "solana":
      return new RegExp(`^${BASE58}{32,44}$`).test(a);
  }
}

/** Example shown as a placeholder in address fields. */
export function addressPlaceholder(family: ChainInfo["family"], mode: NetworkMode): string {
  switch (family) {
    case "evm":
      return "0x…";
    case "tron":
      return "T…";
    case "bitcoin":
      return mode === "testnet" ? "tb1…" : "bc1…";
    case "solana":
      return "Solana address";
  }
}

/** Explorer page of a transaction on this chain. */
export function explorerTxUrl(chain: ChainInfo, hash: string): string {
  return chain.explorerTx.replace("{hash}", encodeURIComponent(hash));
}
